import Icon from 'components/Input/Icon';
import { useTheme } from 'providers/ThemeProvider';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components/macro';
import TooltipControlled from 'components/Tooltip/tooltipControlled';
import utils from 'utils';
import { useSelector, useDispatch } from 'react-redux';
import selectors from 'state/selectors';
import actions from 'state/actions';

const StyledLink = styled(Link)`
  text-decoration: none;
`;

const StyledDiv = styled.div`
  text-decoration: none;
`;

// Gestisci css submenu in modo ricorsivo
const StyledMenuButton = styled.div<{
  isActive: boolean;
  darkMode: boolean;
  isMenuOpen?: boolean;
  noBackground?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: ${({ isMenuOpen }) => (isMenuOpen ? '100%' : '40px')};
  padding-left: 7px;
  font-size: ${({ theme }) => theme.fontSizePalette.medium};
  text-align: left;
  height: 40px;
  border: 1px solid transparent;
  overflow: hidden;
  background-color: ${({ isActive, noBackground, theme }) =>
    isActive &&
    !noBackground &&
    `${
      theme.colorPalette.isDarkMode
        ? theme.colorPalette.withOpacity(theme.colorPalette.turquoise.accent, 0.1)
        : theme.colorPalette.turquoise.background
    }`};
  p {
    color: ${({ isActive, theme, darkMode }) =>
      isActive
        ? darkMode
          ? theme.colorPalette.turquoise.normal
          : theme.colorPalette.turquoise.dark
        : darkMode
        ? theme.colorPalette.grey.grey9
        : theme.colorPalette.grey.grey8};
    font-weight: ${({ isActive, theme }) =>
      isActive ? theme.fontWeightPalette.medium : theme.fontWeightPalette.light};
    padding: 5px 0 4px 6px;
    line-height: 14px;
    letter-spacing: -0.38px;
    font-weight: ${({ isActive, theme }) =>
      isActive ? theme.fontWeightPalette.medium : theme.fontWeightPalette.regular};
  }
  white-space: nowrap;
  border-radius: 8px;
  &:hover {
    cursor: pointer;
    background-color: ${({ theme, isActive }) =>
      !isActive &&
      `${
        theme.colorPalette.isDarkMode
          ? theme.colorPalette.withOpacity(theme.colorPalette.grey.grey9, 0.1)
          : theme.colorPalette.withOpacity(theme.colorPalette.grey.grey5, 0.2)
      }`};
  }
`;

const MenuContainer = styled.div<{ isFirst: boolean }>`
  margin-bottom: ${({ isFirst }) => (isFirst ? '72px' : '20px')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export interface Props {
  isActive: boolean;
  text: string;
  link?: string;
  isFirst?: boolean;
  isMenuOpen?: boolean;
  id: number;
  onClick?: () => void;
}

const customIcons = (number: number, style: any) => {
  const customiconsMap = {
    3: <Icon icon="invoice-management" style={style} />,
    56: <Icon icon="upload" style={style} />,
    31: <Icon icon="acquisition-monitor" style={style} />,
    115: <Icon icon="custom-validation" style={style} />,
    109: <Icon icon="claim-validation" style={style} />,
    10: <Icon icon="report" style={style} />,
    108: <Icon icon="contract-abstraction" style={style} />,
    50: <Icon icon="invoice-workflow" style={style} />,
    110: <Icon icon="rx-validation" style={style} />,
    111: <Icon icon="configurator" style={style} />,
    113: <Icon icon="control-tower" style={style} />,
    112: <Icon icon="archive-icon" style={style} />,
    114: <Icon icon="ml-dashboard" style={style} />,
    116: <Icon icon="archive-icon" style={style} />,
    120: <Icon icon="robot" style={style} />,
    4: <Icon icon="dispatcher" style={style} />,
  };
  return customiconsMap?.[number as keyof typeof customiconsMap] ?? ''; // Default to robot icon if id not found
};

const MenuOption = (props: Props) => {
  const location = useLocation();
  const dispatch = useDispatch();

  const { isActive, text, link, isFirst = false, isMenuOpen = true } = props;
  const { isDarkMode, theme } = useTheme();
  const t = utils.intl.useTranslator();
  const walkthrough = useSelector(selectors.chatbot.getWalkthroughState);

  const techName = utils.converter.techName(text);

  const iconColor = isActive
    ? isDarkMode
      ? theme.colorPalette.turquoise.normal
      : theme.colorPalette.turquoise.dark
    : theme.colorPalette.grey.grey9;

  const shouldTriggerWalkthroughRemount = () => {
    if (!walkthrough.isActive || !link || location.pathname !== link) {
      return false;
    }

    const dataWalkthroughAttr = `sidebar-${techName}`;
    const previousStep = walkthrough.steps.find((s) => s.id === walkthrough.previousStepId);
    const previousStepTargets = previousStep?.uiTarget.split('|').map((t) => t.trim()) || [];

    return previousStepTargets.includes(dataWalkthroughAttr);
  };

  const handleClick = (e: React.MouseEvent) => {
    // Handle walkthrough navigation: force remount when clicking same route during guided tour
    if (shouldTriggerWalkthroughRemount()) {
      e.preventDefault();
      // Increment refreshKey to change Route key prop, triggering clean component remount
      dispatch(actions.chatbot.forceWalkthroughRefresh());
    }

    props.onClick?.();
  };
  const menuButton = (
    <MenuContainer isFirst={isFirst}>
      <StyledMenuButton noBackground={!link} isActive={isActive} darkMode={isDarkMode} isMenuOpen={isMenuOpen}>
        {customIcons(props.id, { color: iconColor })} {isMenuOpen && <p>{text}</p>}
      </StyledMenuButton>
    </MenuContainer>
  );
  if (link)
    return (
      <StyledLink to={link} onClick={handleClick} data-walkthrough={`sidebar-${techName}`}>
        {!isMenuOpen && !isActive ? (
          <TooltipControlled content={t(text)} placement="right" trigger="hover" delayShow={800}>
            {menuButton}
          </TooltipControlled>
        ) : (
          menuButton
        )}
      </StyledLink>
    );

  return (
    <StyledDiv onClick={props.onClick} data-walkthrough={`sidebar-${techName}`}>
      {!isMenuOpen && !isActive ? (
        <TooltipControlled content={t(text)} placement="right" trigger="hover" delayShow={400}>
          {menuButton}
        </TooltipControlled>
      ) : (
        menuButton
      )}
    </StyledDiv>
  );
};

export default MenuOption;
